// Lazy Sentry loader — keeps the ~500KB @sentry/* SDK OUT of the initial JS
// bundle so cold-start parse is dramatically lighter. @sentry/react is imported
// dynamically (its own async chunk) and init runs on idle AFTER first paint.
//
// SAFETY / no-loss guarantee: errors during the pre-load window are NOT lost —
// index.js's window.onerror + unhandledrejection handlers and ErrorBoundary both
// call reportError() → our own audit DB synchronously, independent of Sentry.
// captureException/setUser/setTag below queue against the in-flight load and
// flush once the SDK resolves, so nothing is dropped; they no-op entirely until
// initSentry() is called (e.g. in dev, where we never load the SDK at all).

let modPromise = null;
let mod = null;
let active = false;
let pendingInit = null;

function load() {
  if (!modPromise) {
    modPromise = import(/* webpackChunkName: "sentry" */ '@sentry/react')
      .then((m) => {
        mod = m;
        if (pendingInit) {
          try { m.init(pendingInit); } catch (_) { /* never throw from monitoring */ }
          pendingInit = null;
        }
        return m;
      })
      .catch(() => null);
  }
  return modPromise;
}

// Defer the SDK load to idle so it never blocks first paint. Only call this in
// production — in dev the wrapper stays inert and the chunk is never fetched.
export function initSentry(config) {
  active = true;
  pendingInit = config;
  const schedule = (cb) => (typeof requestIdleCallback === 'function'
    ? requestIdleCallback(cb, { timeout: 4000 })
    : setTimeout(cb, 2000));
  schedule(() => { load(); });
}

function whenReady(fn) {
  if (!active) return; // not initialized (dev) — no-op, never loads the SDK
  if (mod) { try { fn(mod); } catch (_) { /* swallow */ } return; }
  load().then((m) => { if (m) { try { fn(m); } catch (_) { /* swallow */ } } });
}

export function captureException(error, context) {
  whenReady((m) => m.captureException(error, context));
}

export function setUser(user) {
  whenReady((m) => m.setUser(user));
}

export function setTag(key, value) {
  whenReady((m) => m.setTag(key, value));
}
